/**
 * Kimlik doğrulama akış-adımı store'u — kaynak-referans
 * `features/auth/state/Authentication.ts` paritesi (mobx singleton).
 *
 * kaynak-referansta bu store login akışının HANGİ adımda olduğunu (default | mfa)
 * ve aktif MFA challenge'ını (ticket + mevcut yöntemler) global tutar;
 * token/userId'yi SessionManager'a delege eder. TEBHOS'ta bugüne kadar bu
 * bilgi `AuthProvider.login`'in dönüş değeri (`LoginOutcome`) üzerinden
 * sayfa-lokal React state'inde taşınıyordu — global bir karşılığı YOKTU.
 *
 * Adaptasyon kararları:
 *   - `MfaMethods` kaynak-referansta `{totp, webauthn}`; TEBHOS backend'i
 *     (`/auth/login` → `methods`) `{totp, sms, backupCodes}` döner.
 *     webauthn (@simplewebauthn) deferred → alan yok.
 *   - Token/userId delege hedefi SessionManager değil `AccountManagerStore`
 *     (TEBHOS'un token kasası). Store bunları KOPYALAMAZ; sadece
 *     `getAuthToken`/`getCurrentUserId` helper'ları delege eder.
 *   - Kalıcılık YOK (referans-parite): MFA ticket'ı kısa ömürlü, sayfa
 *     yenilenince akış baştan başlar; localStorage'a yazılmaz.
 *
 * KABLOLANDI (çift-yazım — mevcut LoginOutcome/prop akışı bozulmadan):
 *   - `AuthProvider.login` `mfa` dalı → `handleMfaTicketSet`,
 *     `finalizeLoginPayload` → `handleSessionStart`, `signOut` → `handleLogout`.
 *   - `MfaScreen` ticket/methods prop'ları opsiyonel; verilmezse
 *     `AuthenticationStore.mfaChallenge`'dan okur (observer), iptalde
 *     `handleMfaTicketClear` çağırır.
 */

import { makeAutoObservable } from "mobx";
import {
  AccountManagerStore,
  getCurrentAccessToken,
} from "@app/features/auth/state/AccountManagerStore";

export const LoginState = {
  Default: "default",
  Mfa: "mfa",
} as const;

export type LoginState = (typeof LoginState)[keyof typeof LoginState];

/**
 * Aktif MFA challenge'ında kullanılabilir yöntemler.
 * kaynak-referans `{totp, webauthn}` + TEBHOS backend kanalları (sms/backupCodes).
 * AUTH-DENETİM D1 (2026-07-18): webauthn artık deferred DEĞİL — backend
 * `/auth/login` methods.webauthn bayrağını döner (kaynak-referans LoginMfaResult 1-1).
 */
export interface MfaMethods {
  totp: boolean;
  sms: boolean;
  backupCodes: boolean;
  webauthn: boolean;
}

class Authentication {
  /** Login akışının adımı — kaynak-referans `loginState`. */
  loginState: LoginState = LoginState.Default;
  /** Backend'in verdiği kısa ömürlü MFA bileti; yalnız `mfa` adımında dolu. */
  mfaTicket: string | null = null;
  /** Bilet için kullanılabilir doğrulama yöntemleri. */
  mfaMethods: MfaMethods | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** kaynak-referans `isInMfaState` computed'ı. */
  get isInMfaState(): boolean {
    return this.loginState === LoginState.Mfa;
  }

  /** Aktif MFA challenge'ı (ticket + yöntemler) — MfaScreen tüketimi için. */
  get mfaChallenge(): { ticket: string; methods: MfaMethods } | null {
    return this.mfaTicket && this.mfaMethods
      ? { ticket: this.mfaTicket, methods: this.mfaMethods }
      : null;
  }

  /** Login yanıtı MFA istedi — akışı `mfa` adımına geçir (kaynak-referans handleMfaTicketSet). */
  handleMfaTicketSet({ ticket, totp, sms, backupCodes, webauthn }: { ticket: string } & MfaMethods): void {
    this.loginState = LoginState.Mfa;
    this.mfaTicket = ticket;
    this.mfaMethods = { totp, sms, backupCodes, webauthn };
  }

  /** MFA iptal/başarısız — akışı başa döndür (kaynak-referans handleMfaTicketClear). */
  handleMfaTicketClear(): void {
    this.loginState = LoginState.Default;
    this.mfaTicket = null;
    this.mfaMethods = null;
  }

  /**
   * Oturum başladı (token alındı) — akış state'ini sıfırla.
   * kaynak-referansta token'ı da SessionManager'a yazar; TEBHOS'ta token yazımı
   * mevcut `setTokens`/`AccountManagerStore` akışında kalır (dokunulmadı).
   */
  handleSessionStart(): void {
    this.loginState = LoginState.Default;
    this.mfaTicket = null;
    this.mfaMethods = null;
  }

  /** Logout — akış state'ini sıfırla (redirect mevcut signOut akışında). */
  handleLogout(): void {
    this.loginState = LoginState.Default;
    this.mfaTicket = null;
    this.mfaMethods = null;
  }
}

export const AuthenticationStore = new Authentication();

/**
 * kaynak-referans `Authentication.authToken` computed'ı — token kasasına delege.
 * (Store token KOPYALAMAZ; tek kaynak AccountManagerStore.)
 */
export function getAuthToken(): string | null {
  return getCurrentAccessToken();
}

/** kaynak-referans `Authentication.currentUserId` — aktif hesabın id'sine delege. */
export function getCurrentUserId(): string | null {
  return AccountManagerStore.currentAccountId;
}

/** kaynak-referans `Authentication.isAuthenticated` — aktif access token var mı? */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}
